import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  contractLocationLabel,
  createMaintenanceCampaign,
  isOnCallContract,
  enrichContractLocations,
  getContractProjects,
  listMaintenanceContracts,
  listMaintenanceTypes,
  maintenanceContractCustomerLabel,
  maintenanceProjectHeadline,
  maintenanceProjectLocationLabel,
  type ContractLocation,
  type MaintenanceContract,
  type MaintenanceProject,
} from "@/lib/wartungen";
import {
  contractCategoryLabels,
  contractMatchesQuery,
  contractRelationLabel,
  contractSubtitle,
  contractTitle,
  formatSchedule,
  normalizeContractStatus,
} from "./lib/campaignView";
import { ContractStatusBadge } from "./components/ContractCard";

type DateMode = "day" | "month" | "year";
type Step = 1 | 2 | 3;

const STEPS: { n: Step; label: string; hint: string }[] = [
  { n: 1, label: "Vertrag", hint: "Für welchen Vertrag?" },
  { n: 2, label: "Projekt", hint: "Ort und Hero-Projekt" },
  { n: 3, label: "Zeitraum", hint: "Kategorie und Termin" },
];

function validateDate(mode: DateMode, value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (mode === "day") return /^\d{4}-\d{2}-\d{2}$/.test(v);
  if (mode === "month") return /^\d{4}-\d{2}$/.test(v);
  return /^\d{4}$/.test(v) && Number(v) >= 1900 && Number(v) <= 9999;
}

function projectIdOf(p: MaintenanceProject | null | undefined): number {
  return Number(p?.project_id ?? p?.id ?? 0);
}

function SelectableRow({
  selected,
  onSelect,
  title,
  subtitle,
  trailing,
  name,
}: {
  selected: boolean;
  onSelect: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  name: string;
}) {
  return (
    <label
      className={clsx(
        "flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition",
        selected ? "border-accent bg-accent/10" : "border-separator hover:bg-fill-tertiary",
      )}
    >
      <input type="radio" name={name} className="sr-only" checked={selected} onChange={onSelect} />
      <span
        className={clsx(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-accent bg-accent text-label-on-accent" : "border-separator-opaque bg-card",
        )}
        aria-hidden
      >
        {selected ? <CheckIcon className="h-3.5 w-3.5" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-label">{title}</span>
        {subtitle ? <span className="block truncate text-xs text-label-secondary">{subtitle}</span> : null}
      </span>
      {trailing}
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="text-xs uppercase tracking-wide text-label-tertiary">{label}</dt>
      <dd className="min-w-0 truncate text-right text-sm font-medium text-label">{value || "–"}</dd>
    </div>
  );
}

export interface NeueWartungDialogProps {
  open: boolean;
  onClose: () => void;
  /** Vorausgewählter Vertrag (z. B. von der Vertragsseite) – Schritt 1 wird übersprungen. */
  initialContract?: MaintenanceContract | null;
  /** Bereits geladene Verträge (spart einen Roundtrip). */
  contracts?: MaintenanceContract[];
}

export function NeueWartungDialog({ open, onClose, initialContract = null, contracts: preloaded }: NeueWartungDialogProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [contracts, setContracts] = useState<MaintenanceContract[]>(preloaded ?? []);
  const [contractQuery, setContractQuery] = useState("");
  const [selectedContract, setSelectedContract] = useState<MaintenanceContract | null>(null);
  const [contractDetail, setContractDetail] = useState<MaintenanceContract | null>(null);
  const [projects, setProjects] = useState<MaintenanceProject[]>([]);
  const [projectQuery, setProjectQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState<MaintenanceProject | null>(null);
  const [selectedTypeKey, setSelectedTypeKey] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [dateMode, setDateMode] = useState<DateMode>("year");
  const [dateValue, setDateValue] = useState(String(new Date().getFullYear()));
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "info" | "error"; msg: string } | null>(null);
  const projectsToken = useRef(0);

  const reset = useCallback(() => {
    setStep(initialContract ? 2 : 1);
    setContractQuery("");
    setSelectedContract(initialContract);
    setContractDetail(null);
    setProjects([]);
    setProjectQuery("");
    setSelectedProject(null);
    setSelectedTypeKey("");
    setSelectedLocationId(null);
    setDateMode("year");
    setDateValue(String(new Date().getFullYear()));
    setSubmitting(false);
    setFeedback(null);
  }, [initialContract]);

  useEffect(() => {
    if (preloaded && preloaded.length) setContracts(preloaded);
  }, [preloaded]);

  // Nur beim Öffnen zurücksetzen – nicht, wenn sich währenddessen Props (Realtime) ändern.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset();
      if (!preloaded || preloaded.length === 0) {
        setLoadingContracts(true);
        listMaintenanceContracts({ limit: 500 })
          .then(setContracts)
          .catch(() => setContracts([]))
          .finally(() => setLoadingContracts(false));
      }
    }
    wasOpen.current = open;
  }, [open, reset, preloaded]);

  const filteredContracts = useMemo(() => {
    const list = contracts.filter((c) => contractMatchesQuery(c, contractQuery));
    return list.sort((a, b) => {
      const sa = normalizeContractStatus(a.status) === "active" ? 0 : 1;
      const sb = normalizeContractStatus(b.status) === "active" ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return contractTitle(a).localeCompare(contractTitle(b), "de", { numeric: true });
    });
  }, [contracts, contractQuery]);

  /* ─────────── Vertragsdetail + Projekte laden ─────────── */

  const knownLocations: ContractLocation[] = useMemo(() => {
    const source = contractDetail ?? selectedContract;
    return Array.isArray(source?.locations) ? source!.locations!.filter(Boolean) : [];
  }, [contractDetail, selectedContract]);

  const needsLocationChoice = knownLocations.length > 1;
  const effectiveLocationId = useMemo(() => {
    if (selectedLocationId) return selectedLocationId;
    if (knownLocations.length === 1) {
      const id = Number(knownLocations[0]?.hero_address_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    }
    return null;
  }, [selectedLocationId, knownLocations]);

  const loadProjects = useCallback(async () => {
    if (!selectedContract?.id) return;
    const token = ++projectsToken.current;
    setLoadingProjects(true);
    try {
      const q = projectQuery.trim();
      const res = await getContractProjects(selectedContract.id, {
        limit: 50,
        q: q.length >= 2 ? q : undefined,
        hero_address_id: effectiveLocationId ?? undefined,
      });
      if (token !== projectsToken.current) return;
      const contract = res.contract || null;
      if (contract) {
        const enriched = await enrichContractLocations(
          Array.isArray(contract.locations) ? contract.locations : [],
          contract.hero_customer_id,
        );
        if (token !== projectsToken.current) return;
        setContractDetail({ ...contract, locations: enriched });
        if (enriched.length > 1 && !selectedLocationId) {
          setProjects([]);
          return;
        }
      }
      setProjects(res.projects || []);
    } catch {
      if (token === projectsToken.current) setProjects([]);
    } finally {
      if (token === projectsToken.current) setLoadingProjects(false);
    }
  }, [selectedContract, projectQuery, effectiveLocationId, selectedLocationId]);

  useEffect(() => {
    if (!open || step !== 2) return;
    const t = window.setTimeout(() => void loadProjects(), 200);
    return () => window.clearTimeout(t);
  }, [open, step, loadProjects]);

  const detailTypes = useMemo(() => {
    const source = contractDetail ?? selectedContract;
    return (Array.isArray(source?.maintenance_types) ? source!.maintenance_types! : []).filter((t) => t.key);
  }, [contractDetail, selectedContract]);

  useEffect(() => {
    if (step === 3 && detailTypes.length === 1 && !selectedTypeKey) {
      setSelectedTypeKey(String(detailTypes[0]?.key));
    }
  }, [step, detailTypes, selectedTypeKey]);

  const dateValid = validateDate(dateMode, dateValue);
  const nextDisabled =
    submitting ||
    (step === 1 && !selectedContract) ||
    (step === 2 && !selectedProject) ||
    (step === 3 && !(selectedTypeKey && dateValid));

  function selectContract(c: MaintenanceContract) {
    setSelectedContract(c);
    setContractDetail(null);
    setSelectedProject(null);
    setSelectedLocationId(null);
    setSelectedTypeKey("");
    setProjects([]);
  }

  async function submit() {
    if (!selectedContract || !selectedProject || !selectedTypeKey || !dateValid) {
      setFeedback({ tone: "error", msg: "Bitte alle Pflichtangaben ausfüllen." });
      return;
    }
    setSubmitting(true);
    setFeedback({ tone: "info", msg: "Wartung wird angelegt …" });
    try {
      const types = await listMaintenanceTypes();
      const match = types.find((t) => String(t.key || "").trim() === selectedTypeKey.trim());
      const typeId = Number(match?.id);
      if (!Number.isFinite(typeId) || typeId <= 0) throw new Error("Wartungskategorie konnte nicht aufgelöst werden.");

      const heroAddressId = effectiveLocationId;
      const projectId = projectIdOf(selectedProject);
      const res = await createMaintenanceCampaign({
        maintenance_type_id: typeId,
        project_id: projectId,
        contract_id: Number(selectedContract.id),
        name: String(selectedProject.beschreibung || `Wartung ${dateValue}`).trim(),
        date_mode: dateMode,
        date_value: dateValue,
        hero_address_id: heroAddressId,
      });
      const campaignId = Number(res.campaign?.id);
      if (!Number.isFinite(campaignId) || campaignId <= 0) throw new Error("Wartung konnte nicht erstellt werden.");

      const usp = new URLSearchParams();
      usp.set("type_key", selectedTypeKey);
      usp.set("project_id", String(projectId));
      usp.set("contract_id", String(selectedContract.id));
      usp.set("campaign_id", String(campaignId));
      usp.set("date_mode", dateMode);
      usp.set("date_value", dateValue);
      if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
      const projectLocation = maintenanceProjectLocationLabel(selectedProject);
      if (projectLocation) usp.set("base_location_name", projectLocation);
      if (selectedProject.beschreibung) usp.set("maintenance_title", String(selectedProject.beschreibung));
      usp.set("fresh", "1");

      onClose();
      navigate(`/wartungen/huelle-detail?${usp.toString()}`);
    } catch (err) {
      setFeedback({
        tone: "error",
        msg:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Wartung konnte nicht erstellt werden.",
      });
      setSubmitting(false);
    }
  }

  function handleNext() {
    if (step === 1 && selectedContract) {
      setStep(2);
      setProjectQuery("");
    } else if (step === 2 && selectedProject) {
      setStep(3);
    } else if (step === 3) {
      void submit();
    }
  }

  function handleBack() {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(initialContract ? 2 : 1);
  }

  const selectedTypeLabel =
    detailTypes.find((t) => String(t.key) === selectedTypeKey)?.name || selectedTypeKey || "";
  const selectedLocationLabel = effectiveLocationId
    ? contractLocationLabel(
        knownLocations.find((l) => Number(l.hero_address_id) === effectiveLocationId),
        effectiveLocationId,
      )
    : knownLocations.length === 0
      ? "–"
      : "";

  const canGoBack = step === 3 || (step === 2 && !initialContract);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={selectedProject != null || (selectedContract != null && !initialContract)}
      title="Neue Wartung anlegen"
      description="Eine Wartung verbindet einen Vertrag mit einem Hero-Projekt und einem Zeitraum. Danach erfasst du die Objekte und führst die Wartung je Objekt durch."
      size="xl"
      bodyClassName="space-y-4"
      footer={
        <>
          {canGoBack ? (
            <Button type="button" variant="ghost" onClick={handleBack} disabled={submitting}>
              Zurück
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="button" loading={submitting} disabled={nextDisabled} onClick={handleNext}>
            {step === 3 ? "Wartung anlegen & öffnen" : "Weiter"}
          </Button>
        </>
      }
    >
      <ol className="grid grid-cols-3 gap-2" aria-label="Schritte">
        {STEPS.map((s) => {
          const done = step > s.n || (s.n === 1 && Boolean(initialContract));
          const active = step === s.n;
          return (
            <li
              key={s.n}
              className={clsx(
                "flex items-center gap-2.5 rounded-xl border px-3 py-2",
                active ? "border-accent bg-accent/10" : "border-separator",
                !active && !done && "opacity-60",
              )}
              aria-current={active ? "step" : undefined}
            >
              <span
                className={clsx(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  done
                    ? "bg-status-green text-label-on-accent"
                    : active
                      ? "bg-accent text-label-on-accent"
                      : "bg-fill-tertiary text-label-secondary",
                )}
              >
                {done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden /> : s.n}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-label">{s.label}</span>
                <span className="hidden truncate text-xs text-label-tertiary sm:block">{s.hint}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {step === 1 ? (
        <div className="space-y-2">
          <p className="text-sm text-label-secondary">
            Wähle den Wartungsvertrag oder einen Eintrag auf Zuruf. Aktive Einträge stehen oben.
          </p>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary" aria-hidden />
            <input
              id="nm-contract-search"
              type="search"
              className="eh-input pl-9"
              placeholder="Name, Kunde, Ort oder Vertragsnummer suchen …"
              value={contractQuery}
              onChange={(e) => setContractQuery(e.target.value)}
              autoFocus
            />
          </div>
          <p className="text-xs text-label-tertiary">
            {loadingContracts ? "Lade Einträge …" : `${filteredContracts.length} von ${contracts.length}`}
          </p>
          <div className="max-h-80 space-y-1.5 overflow-auto pr-1">
            {filteredContracts.map((c) => {
              const categories = contractCategoryLabels(c);
              return (
                <SelectableRow
                  key={c.id}
                  name="nm-contract"
                  selected={selectedContract?.id === c.id}
                  onSelect={() => selectContract(c)}
                  title={
                    <>
                      {contractTitle(c)}
                      {isOnCallContract(c) ? (
                        <span className="ml-1.5 rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label-secondary">
                          Auf Zuruf
                        </span>
                      ) : contractSubtitle(c) ? (
                        <span className="ml-1.5 text-xs font-normal text-label-tertiary">{contractSubtitle(c)}</span>
                      ) : null}
                    </>
                  }
                  subtitle={[
                    maintenanceContractCustomerLabel(c),
                    (Array.isArray(c.locations) ? c.locations.length : 0) > 1
                      ? `${c.locations!.length} Orte`
                      : contractLocationLabel(c.locations?.[0]),
                    categories.length ? categories.join(", ") : "keine Kategorie",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  trailing={<ContractStatusBadge status={c.status} />}
                />
              );
            })}
            {!loadingContracts && filteredContracts.length === 0 ? (
              <p className="px-1 py-4 text-center text-sm text-label-tertiary">Kein Eintrag passt zur Suche.</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-3">
          <div className="rounded-xl bg-fill-tertiary/60 px-3 py-2 text-sm">
            <span className="text-label-secondary">{contractRelationLabel(selectedContract)}: </span>
            <strong className="text-label">{contractTitle(selectedContract)}</strong>
            {contractSubtitle(selectedContract) ? (
              <span className="text-label-tertiary"> ({contractSubtitle(selectedContract)})</span>
            ) : null}
            {maintenanceContractCustomerLabel(selectedContract) ? (
              <span className="text-label-secondary"> · {maintenanceContractCustomerLabel(selectedContract)}</span>
            ) : null}
          </div>

          {needsLocationChoice ? (
            <div>
              <p className="eh-label">
                Ort <span className="text-status-red">*</span>
              </p>
              <p className="mb-2 text-xs text-label-secondary">
                {isOnCallContract(selectedContract)
                  ? "Dieser Eintrag umfasst mehrere Orte. Wähle den Ort, an dem die Wartung stattfindet."
                  : "Der Vertrag umfasst mehrere Orte. Wähle den Ort, an dem die Wartung stattfindet."}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {knownLocations.map((loc) => {
                  const id = Number(loc.hero_address_id);
                  const active = selectedLocationId === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      aria-pressed={active}
                      className={clsx(
                        "rounded-full px-3 py-1.5 text-sm font-medium transition",
                        active
                          ? "bg-accent text-label-on-accent"
                          : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
                      )}
                      onClick={() => {
                        setSelectedLocationId(id);
                        setSelectedProject(null);
                      }}
                    >
                      {contractLocationLabel(loc, id)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className={clsx(needsLocationChoice && !selectedLocationId && "pointer-events-none opacity-50")}>
            <label className="eh-label" htmlFor="nm-project-search">
              Hero-Projekt <span className="text-status-red">*</span>
            </label>
            <p className="mb-2 text-xs text-label-secondary">
              Die Wartung wird auf dieses Projekt gebucht. Suche nach Auftragsnummer oder Beschreibung.
            </p>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary" aria-hidden />
              <input
                id="nm-project-search"
                type="search"
                className="eh-input pl-9"
                placeholder="Auftragsnummer oder Beschreibung …"
                value={projectQuery}
                onChange={(e) => setProjectQuery(e.target.value)}
              />
            </div>
            <p className="mt-1.5 flex items-center gap-2 text-xs text-label-tertiary">
              {loadingProjects ? (
                <>
                  <Spinner /> Lade Projekte …
                </>
              ) : needsLocationChoice && !selectedLocationId ? (
                "Bitte zuerst einen Ort wählen."
              ) : (
                `${projects.length} Projekt${projects.length === 1 ? "" : "e"} gefunden`
              )}
            </p>
            <div className="mt-2 max-h-64 space-y-1.5 overflow-auto pr-1">
              {projects.map((p) => {
                const pid = projectIdOf(p);
                return (
                  <SelectableRow
                    key={pid}
                    name="nm-project"
                    selected={projectIdOf(selectedProject) === pid}
                    onSelect={() => setSelectedProject(p)}
                    title={maintenanceProjectHeadline(p) || `Projekt ${pid}`}
                    subtitle={[p.beschreibung, maintenanceProjectLocationLabel(p)].filter(Boolean).join(" · ")}
                  />
                );
              })}
              {!loadingProjects && projects.length === 0 && !(needsLocationChoice && !selectedLocationId) ? (
                <p className="px-1 py-4 text-center text-sm text-label-tertiary">
                  Kein Projekt gefunden. Prüfe die Suche oder lege das Projekt zuerst in Hero an.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="grid gap-4 md:grid-cols-[1fr_16rem]">
          <div className="space-y-4">
            {detailTypes.length > 1 ? (
              <div>
                <p className="eh-label">
                  Wartungskategorie <span className="text-status-red">*</span>
                </p>
                <p className="mb-2 text-xs text-label-secondary">
                  Bestimmt das Formular und die Objektart (z. B. Brandschutztüren, RWA).
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detailTypes.map((t) => {
                    const active = selectedTypeKey === String(t.key);
                    return (
                      <button
                        key={t.key}
                        type="button"
                        aria-pressed={active}
                        className={clsx(
                          "rounded-full px-3 py-1.5 text-sm font-medium transition",
                          active
                            ? "bg-accent text-label-on-accent"
                            : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
                        )}
                        onClick={() => setSelectedTypeKey(String(t.key))}
                      >
                        {t.name || t.key}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : detailTypes.length === 0 ? (
              <p className="rounded-xl border border-status-orange/40 bg-status-orange/10 px-3 py-2 text-sm text-label">
                {isOnCallContract(selectedContract)
                  ? "Diesem Eintrag ist keine Wartungskategorie zugewiesen. Bitte zuerst eine Kategorie hinterlegen."
                  : "Dem Vertrag ist keine Wartungskategorie zugewiesen. Bitte zuerst im Vertrag eine Kategorie hinterlegen."}
              </p>
            ) : null}

            <div>
              <p className="eh-label">
                Wartungszeitraum <span className="text-status-red">*</span>
              </p>
              <p className="mb-2 text-xs text-label-secondary">
                Wie genau ist der Termin bekannt? Für jährliche Wartungen reicht das Jahr; der Zeitraum erscheint
                später in Übersicht und Protokoll.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <SegmentedControl<DateMode>
                  name="Genauigkeit"
                  size="md"
                  value={dateMode}
                  onChange={(mode) => {
                    setDateMode(mode);
                    const y = dateValue.match(/^(\d{4})/)?.[1] ?? String(new Date().getFullYear());
                    if (mode === "year") setDateValue(y);
                    else if (mode === "month") setDateValue(`${y}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
                    else setDateValue("");
                  }}
                  options={[
                    { value: "year", label: "Jahr" },
                    { value: "month", label: "Monat" },
                    { value: "day", label: "Genauer Tag" },
                  ]}
                />
                <input
                  id="nm-date-value"
                  aria-label="Wartungsdatum"
                  type={dateMode === "day" ? "date" : dateMode === "month" ? "month" : "number"}
                  min={dateMode === "year" ? 1900 : undefined}
                  max={dateMode === "year" ? 9999 : undefined}
                  placeholder={dateMode === "year" ? "JJJJ" : undefined}
                  className="eh-input w-44"
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                />
              </div>
              {!dateValid && dateValue ? (
                <p className="mt-1 text-xs text-status-red">Bitte ein gültiges Datum eingeben.</p>
              ) : null}
            </div>
          </div>

          <aside className="rounded-xl border border-separator bg-card-elevated/40 px-3.5 py-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Zusammenfassung</h3>
            <dl className="mt-1 divide-y divide-separator/60">
              <SummaryRow label={contractRelationLabel(selectedContract)} value={contractTitle(selectedContract)} />
              <SummaryRow label="Kunde" value={maintenanceContractCustomerLabel(selectedContract)} />
              <SummaryRow label="Ort" value={selectedLocationLabel} />
              <SummaryRow label="Projekt" value={maintenanceProjectHeadline(selectedProject)} />
              <SummaryRow label="Kategorie" value={selectedTypeLabel} />
              <SummaryRow label="Zeitraum" value={dateValid ? formatSchedule(dateMode, dateValue) : ""} />
            </dl>
            <p className="mt-3 text-xs text-label-tertiary">
              Nach dem Anlegen öffnet sich die Wartung. Dort legst du Objekte an und startest je Objekt das Formular.
            </p>
          </aside>
        </div>
      ) : null}

      {feedback ? (
        <p
          className={clsx("text-sm", feedback.tone === "error" ? "text-status-red" : "text-label-secondary")}
          role={feedback.tone === "error" ? "alert" : undefined}
        >
          {feedback.msg}
        </p>
      ) : null}
    </Dialog>
  );
}
